import { createContext, useContext, useRef, useState } from 'react';

import { cn } from 'helpers/classnames';
import useMartyContext from 'hooks/useMartyContext';
import {
  type ColorTwisterContextValue,
  type ColorTwisterItemProps,
  type ColorTwisterItemsProps,
  type ColorTwisterModeType,
  type ColorTwisterRootProps,
  type ColorTwisterToggleProps
} from 'components/productdetail/ColorTwister/ColorTwister.types';

const ColorTwisterContext = createContext<ColorTwisterContextValue | null>(null);

const useColorTwister = () => {
  const context = useContext(ColorTwisterContext);
  if (!context) {
    throw new Error('useColorTwister must be used within an ColorTwister.Root');
  }
  return context;
};

/**
 * Root component that manages expansion state and provides context to children
 * Controls whether items are displayed in a grid or horizontal scroll layout
 */
const ColorTwisterRoot = ({ initialMode = 'carousel', ...rest }: React.PropsWithChildren<ColorTwisterRootProps>) => {
  const [mode, setMode] = useState<ColorTwisterModeType>(initialMode);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const contextValue = {
    isCarouselMode: mode === 'carousel',
    isGridMode: mode === 'grid',
    toggleMode: () => setMode(previousMode => (previousMode === 'carousel' ? 'grid' : 'carousel')),
    containerRef
  };

  return <ColorTwisterContext.Provider value={contextValue} {...rest} />;
};

/**
 * Toggle button that switches between expanded (grid) and collapsed (carousel) states
 * Shows count of total items when collapsed
 */
const ColorTwisterToggle = ({ className, itemCount, onToggleChange, ...rest }: React.PropsWithChildren<ColorTwisterToggleProps>) => {
  const { isGridMode, toggleMode } = useColorTwister();
  const { testId } = useMartyContext();

  const action = isGridMode ? 'collapsed' : 'expanded';

  return (
    <button
      type="button"
      onClick={() => {
        if (onToggleChange) {
          onToggleChange(action);
        }

        toggleMode();
      }}
      className={cn('shrink-0 underline underline-offset-2', className)}
      data-test-id={testId('colorTwisterToggle')}
      {...rest}
    >
      {isGridMode ? 'See Less' : `See All ${itemCount}`}
    </button>
  );
};

/**
 * Container for color swatch items that handles layout switching between grid and carousel
 * Manages CSS calculations for proper item sizing and spacing
 */
const ColorTwisterItems = ({ className, columnCount, ...rest }: React.PropsWithChildren<ColorTwisterItemsProps>) => {
  const { isGridMode, containerRef } = useColorTwister();
  const { testId } = useMartyContext();

  return (
    <div className="overflow-hidden">
      <div
        ref={containerRef}
        className={cn(
          'scrollbar-hidden relative flex items-center gap-[var(--column-gap)] overscroll-none',
          isGridMode ? 'flex-wrap' : 'overflow-x-auto',
          className
        )}
        style={
          {
            ['--column-gap']: '4px', // Default gap between items
            ['--column-count']: columnCount, // Default number of columns
            ['--initial-column-width']: 'calc(100% / var(--column-count))', // Base width of each column
            ['--gap-adjustment']: 'calc(var(--column-gap) * (var(--column-count) - 1) / var(--column-count))', // Adjustment for gaps
            ['--column-width']: 'calc(var(--initial-column-width) - var(--gap-adjustment))' // Final width of each column accounting for gaps
          } as React.CSSProperties
        }
        data-test-id={testId('colorTwisterItems')}
        {...rest}
      />
    </div>
  );
};

/**
 * Individual color swatch container that handles horizontal scrolling behavior
 * When shouldScroll is true, this item will be centered in the horizontal scroll container
 */
const ColorTwisterItem = ({ shouldScroll = false, className, ...rest }: React.PropsWithChildren<ColorTwisterItemProps>) => {
  const { isCarouselMode, containerRef } = useColorTwister();

  return (
    <div
      ref={node => {
        if (isCarouselMode && shouldScroll) {
          const container = containerRef.current;
          if (!container || !node) return;

          // Calculate position to center the item horizontally
          const scrollPosition = Math.max(0, node.offsetLeft - (container.offsetWidth - node.offsetWidth) / 2);

          requestAnimationFrame(() => {
            container.scrollTo({
              left: scrollPosition,
              behavior: 'smooth'
            });
          });
        }
      }}
      className={cn('shrink-0 basis-[--column-width]', className)}
      {...rest}
    />
  );
};

export const ColorTwister = {
  Root: ColorTwisterRoot,
  Toggle: ColorTwisterToggle,
  Items: ColorTwisterItems,
  Item: ColorTwisterItem,
  useColorTwister: useColorTwister
};
